import { FC, useMemo } from "react";
import { PageWrapper } from "@connectedComponents";
import { stackEnum } from "@utilities/enums";
import { useController, PromotionFilterEnum } from "./useController";
import { Button, Card, GenericActionsBar, Icon, Modal } from "@components";
import { AddPromotion, PromotionCard, AddPromoCode } from "./components";
import { isPast, isFuture } from "date-fns";

export const PromotionManagement: FC = () => {

    const {
        createCampaignModal, createPromoCodeModal,
        onToggleCampaignModal, onTogglePromoCodeModal,
        filteredPromotions, allPromotions,
        handleCreatePromotion, goBack, filterType,
        setSearchPromotion, handleEditPromotion,
        handleDeletePromotion,
        handleFilterChange, handleToggleActivePromotion,
        allCategories, allProducts,
    } = useController();

    // Statistiques des promotions
    const stats = useMemo(() => {
        const now = new Date();
        const active = allPromotions.filter(p =>
            !p.isArchived && p.isActive &&
            !isPast(new Date(p.endDate)) &&
            !isFuture(new Date(p.startDate))
        ).length;
        const expired = allPromotions.filter(p =>
            !p.isArchived && isPast(new Date(p.endDate))
        ).length;
        const upcoming = allPromotions.filter(p =>
            !p.isArchived && isFuture(new Date(p.startDate))
        ).length;
        const archived = allPromotions.filter(p => p.isArchived).length;

        return { active, expired, upcoming, archived, total: allPromotions.length };
    }, [allPromotions]);

    return (
        <PageWrapper
            activeStack={stackEnum.stocksStack}
            handleGoBack={goBack}
        >
            <Card
                title="Gestion des promotions/remises"
                actions={
                    <GenericActionsBar
                        searchPlaceholder="Rechercher une promotion..."
                        handleSearch={setSearchPromotion}
                        customActions={[
                            <Button
                                className="rounded-btn gap-1"
                                variant='primary'
                                onClick={onToggleCampaignModal}
                                key="campaign"
                            >
                                <Icon iconName='Megaphone' size={18} />
                                <span className="hidden sm:inline">Campagne</span>
                            </Button>,
                            <Button
                                className="rounded-btn gap-1"
                                variant='success'
                                onClick={onTogglePromoCodeModal}
                                key="promocode"
                            >
                                <Icon iconName='Tag' size={18} />
                                <span className="hidden sm:inline">Code promo</span>
                            </Button>
                        ]}
                    />
                }
            >
                {/* Statistiques */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 mb-6">
                    <div
                        className={`bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-3 border border-green-200 cursor-pointer transition-all hover:shadow-md ${filterType === PromotionFilterEnum.ACTIVE ? 'ring-2 ring-green-500' : ''}`}
                        onClick={() => handleFilterChange(PromotionFilterEnum.ACTIVE)}
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-2xl font-bold text-green-700">{stats.active}</p>
                                <p className="text-xs text-green-600">Actives</p>
                            </div>
                            <div className="w-10 h-10 bg-green-200 rounded-full flex items-center justify-center">
                                <Icon iconName="CheckCircle" size={20} className="text-green-700" />
                            </div>
                        </div>
                    </div>
                    <div
                        className={`bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-3 border border-blue-200 cursor-pointer transition-all hover:shadow-md ${filterType === PromotionFilterEnum.INACTIVE ? 'ring-2 ring-blue-500' : ''}`}
                        onClick={() => handleFilterChange(PromotionFilterEnum.INACTIVE)}
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-2xl font-bold text-blue-700">{stats.upcoming + stats.expired}</p>
                                <p className="text-xs text-blue-600">Inactives</p>
                            </div>
                            <div className="w-10 h-10 bg-blue-200 rounded-full flex items-center justify-center">
                                <Icon iconName="PauseCircle" size={20} className="text-blue-700" />
                            </div>
                        </div>
                    </div>
                    <div
                        className={`bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-3 border border-orange-200 cursor-pointer transition-all hover:shadow-md ${filterType === PromotionFilterEnum.ARCHIVED ? 'ring-2 ring-orange-500' : ''}`}
                        onClick={() => handleFilterChange(PromotionFilterEnum.ARCHIVED)}
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-2xl font-bold text-orange-700">{stats.archived}</p>
                                <p className="text-xs text-orange-600">Archivées</p>
                            </div>
                            <div className="w-10 h-10 bg-orange-200 rounded-full flex items-center justify-center">
                                <Icon iconName="Archive" size={20} className="text-orange-700" />
                            </div>
                        </div>
                    </div>
                    <div
                        className={`bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-3 border border-gray-200 cursor-pointer transition-all hover:shadow-md ${filterType === PromotionFilterEnum.ALL ? 'ring-2 ring-gray-500' : ''}`}
                        onClick={() => handleFilterChange(PromotionFilterEnum.ALL)}
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-2xl font-bold text-gray-700">{stats.total}</p>
                                <p className="text-xs text-gray-600">Total</p>
                            </div>
                            <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                                <Icon iconName="Tag" size={20} className="text-gray-700" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Liste des promotions */}
                {
                    filteredPromotions.length > 0 ? (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {
                                filteredPromotions.map((promotion, i) => (
                                    <PromotionCard
                                        key={i}
                                        promotion={promotion}
                                        categories={allCategories}
                                        products={allProducts}
                                        onEdit={handleEditPromotion}
                                        onDelete={handleDeletePromotion}
                                        onToggleActive={handleToggleActivePromotion}
                                    />
                                ))
                            }
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-12 text-center">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                                <Icon iconName="Tag" size={32} className="text-gray-400" />
                            </div>
                            <p className="text-lg font-semibold text-gray-500 mb-2">Aucune promotion trouvée</p>
                            <p className="text-sm text-gray-400 mb-4">
                                {filterType !== PromotionFilterEnum.ALL
                                    ? "Essayez de changer le filtre ou la recherche"
                                    : "Créez votre première promotion pour commencer"
                                }
                            </p>
                            {filterType === PromotionFilterEnum.ALL && (
                                <div className="flex gap-2">
                                    <Button
                                        variant="primary"
                                        onClick={onToggleCampaignModal}
                                        className="gap-1"
                                    >
                                        <Icon iconName="Megaphone" size={18} />
                                        Campagne
                                    </Button>
                                    <Button
                                        variant="success"
                                        onClick={onTogglePromoCodeModal}
                                        className="gap-1"
                                    >
                                        <Icon iconName="Tag" size={18} />
                                        Code promo
                                    </Button>
                                </div>
                            )}
                        </div>
                    )
                }
            </Card>

            {/* Modal pour créer une campagne */}
            <Modal
                isOpen={createCampaignModal}
                onClose={onToggleCampaignModal}
                title="Créer une campagne promotionnelle"
            >
                <AddPromotion
                    onCancel={onToggleCampaignModal}
                    onCreatePromotion={handleCreatePromotion}
                    categories={allCategories}
                    products={allProducts}
                />
            </Modal>

            {/* Modal pour créer un code promo */}
            <Modal
                isOpen={createPromoCodeModal}
                onClose={onTogglePromoCodeModal}
                title="Créer un code promo"
            >
                <AddPromoCode
                    onCancel={onTogglePromoCodeModal}
                    onCreatePromotion={handleCreatePromotion}
                />
            </Modal>
        </PageWrapper>
    );
};
